import { MoreHorizontal, Trash2, UserPlus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { LetterAvatar, Loading } from '@/components/shared';
import { Button } from '@/components/ui/NewButton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import useAllUsers from '@/hooks/useAllUsers';
import useMatterMembers from '@/hooks/useMatterMembers';
import { apiErrorMessage, backendClient } from '@/lib/api-client';
import { fullName } from '@/lib/user';
import type { MatterMember } from '@/types/base';

export function MatterMembersSheet({
  matterGuid,
  matterName,
  caseReference,
  open,
  onOpenChange,
  onMembersChanged,
}: {
  matterGuid: string;
  matterName: string;
  caseReference: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMembersChanged?: () => void | Promise<void>;
}) {
  const { members, isLoading, isError, mutateMatterMembers } = useMatterMembers(
    open ? matterGuid : undefined,
  );
  const { users, isLoading: usersLoading, isError: usersError } = useAllUsers();
  const [addOpen, setAddOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<string>('');
  const [role, setRole] = useState<'Member' | 'Owner'>('Member');
  const [isAdding, setIsAdding] = useState(false);
  const [removingGuid, setRemovingGuid] = useState<string | null>(null);
  const [memberToRemove, setMemberToRemove] = useState<MatterMember | null>(null);

  useEffect(() => {
    if (!open) {
      setAddOpen(false);
      setSelectedEmail('');
      setRole('Member');
      setMemberToRemove(null);
    }
  }, [open]);

  const availableUsers = useMemo(() => {
    const memberGuids = new Set(members.map((m) => m.user_guid));
    const memberEmails = new Set(members.map((m) => m.email.toLowerCase()));
    return users.filter(
      (u) => !memberGuids.has(u.guid) && !memberEmails.has(u.email.toLowerCase()),
    );
  }, [users, members]);

  const ownerCount = members.filter((m) => m.role === 'Owner').length;

  const refresh = async () => {
    await mutateMatterMembers();
    await onMembersChanged?.();
  };

  const handleAdd = async () => {
    if (!selectedEmail) {
      toast.error('Select a user to add');
      return;
    }
    setIsAdding(true);
    try {
      await backendClient.post(`/matter/${matterGuid}/members`, {
        email: selectedEmail,
        role,
      });
      toast.success('Member added');
      setAddOpen(false);
      setSelectedEmail('');
      setRole('Member');
      await refresh();
    } catch (error) {
      toast.error(apiErrorMessage(error) || 'Failed to add member');
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemove = async () => {
    if (!memberToRemove) return;
    setRemovingGuid(memberToRemove.user_guid);
    try {
      await backendClient.delete(`/matter/${matterGuid}/members`, {
        params: { member_guid: memberToRemove.user_guid },
      });
      toast.success('Member removed');
      setMemberToRemove(null);
      await refresh();
    } catch (error) {
      toast.error(apiErrorMessage(error) || 'Failed to remove member');
    } finally {
      setRemovingGuid(null);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="flex w-full flex-col sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Matter Members</SheetTitle>
            <SheetDescription>
              {matterName}
              {caseReference ? ` · ${caseReference}` : ''}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4 flex items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              {members.length} {members.length === 1 ? 'member' : 'members'}
            </p>
            <Button
              size="sm"
              variant="outline"
              icon={UserPlus}
              text="Add member"
              onClick={() => setAddOpen(true)}
            />
          </div>

          <div className="mt-4 min-h-0 flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex h-40 items-center justify-center">
                <Loading />
              </div>
            ) : isError ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Could not load members.
              </p>
            ) : members.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No members on this matter yet.
              </p>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Member</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead className="w-12">
                        <span className="sr-only">Actions</span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {members.map((member) => {
                      const isLastOwner = member.role === 'Owner' && ownerCount <= 1;
                      return (
                        <TableRow key={member.user_guid}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <LetterAvatar name={member.name} />
                              <div className="min-w-0">
                                <p className="truncate text-sm font-medium">{member.name}</p>
                                <p className="truncate text-xs text-muted-foreground">{member.email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">{member.role}</TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  icon={MoreHorizontal}
                                  aria-label={`Actions for ${member.name}`}
                                  disabled={removingGuid === member.user_guid}
                                />
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  className="text-red-600 focus:text-red-600"
                                  disabled={isLastOwner}
                                  onSelect={() => setMemberToRemove(member)}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  {isLastOwner ? 'Cannot remove last owner' : 'Remove'}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add member</DialogTitle>
            <DialogDescription>
              Add a firm user to {matterName}.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <label className="text-sm font-medium">User</label>
              <Select
                value={selectedEmail}
                onValueChange={setSelectedEmail}
                disabled={usersLoading || Boolean(usersError) || availableUsers.length === 0}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      usersLoading
                        ? 'Loading users…'
                        : usersError
                          ? 'Could not load users'
                          : availableUsers.length === 0
                            ? 'No users available to add'
                            : 'Select a user'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {availableUsers.map((user) => (
                    <SelectItem key={user.guid} value={user.email}>
                      {fullName(user)} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {usersError ? (
                <p className="text-sm text-muted-foreground">
                  Could not load the user list. Close and try again.
                </p>
              ) : null}
            </div>
            <div className="grid gap-2">
              <label className="text-sm font-medium">Role</label>
              <Select
                value={role}
                onValueChange={(value) => setRole(value as 'Member' | 'Owner')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Member">Member</SelectItem>
                  <SelectItem value="Owner">Owner</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" text="Cancel" onClick={() => setAddOpen(false)} disabled={isAdding} />
            <Button
              text="Add"
              loadingText="Adding"
              loading={isAdding}
              disabled={!selectedEmail || isAdding}
              onClick={handleAdd}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!memberToRemove} onOpenChange={(next) => { if (!next) setMemberToRemove(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remove member?</DialogTitle>
            <DialogDescription>
              {memberToRemove
                ? `Remove ${memberToRemove.name} from ${matterName}? They will lose access to this matter.`
                : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              text="Cancel"
              onClick={() => setMemberToRemove(null)}
              disabled={!!removingGuid}
            />
            <Button
              variant="destructive"
              text="Remove"
              loadingText="Removing"
              loading={!!removingGuid}
              onClick={handleRemove}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
